import * as Yup from 'yup';
import { useAlertHook } from 'app/hooks/useAlertHook';
import { useTranslation } from 'react-i18next';
import { Modal } from '../Modal';
import { Button } from '../Button';
import { Dispatch, SetStateAction, useEffect, useMemo } from 'react';
import { Field, Form, Formik } from 'formik';
import {
  useCreateShipNoteMutation,
  useDeleteNoteMutation,
  useEditShipNoteMutation,
  useGetNotesListMutation,
  useGetShipNoteQuery,
} from 'app/api/facade';
import styles from './ShipNoteModal.module.scss';
import clsx from 'clsx';


interface ShipNoteModalProps {
  modalState: [boolean, Dispatch<SetStateAction<boolean>>];
  mmsi: number | undefined;
}

export const ShipNoteModal = ({ modalState, mmsi }: ShipNoteModalProps) => {
  const { t } = useTranslation();
  const { addAlert } = useAlertHook();
  const [showModal, setShowModal] = modalState;

  const { data } = useGetShipNoteQuery(mmsi, { skip: typeof mmsi !== 'number' });
  const note = data?.notes?.[0] || null;

  const isEditMode = Boolean(note);

  const [editNote, editRequest] = useEditShipNoteMutation();
  const [deleteNote, deleteRequest] = useDeleteNoteMutation();
  const [createNote, createRequest] = useCreateShipNoteMutation();
  const [, notesRequest] = useGetNotesListMutation({
    fixedCacheKey: 'get-notes-mutation',
  });
  useEffect(() => {
    if (editRequest.isError) {
      addAlert(editRequest.error);
    }
    if (editRequest.isSuccess) {
      notesRequest.reset();
      addAlert({
        data: {
          title: 'Success',
          status: 200,
          detail: 'Success',
          messages: [{ description: t('ChangesSaved'), type: 'information' }],
        },
      });
    }
  }, [editRequest]);

  useEffect(() => {
    if (deleteRequest.isError) {
      addAlert(deleteRequest.error);
    }
    if (deleteRequest.isSuccess) {
      notesRequest.reset();

      addAlert({
        data: {
          title: 'Success',
          status: 200,
          detail: 'Success',
          messages: [{ description: t('ChangesSaved'), type: 'information' }],
        },
      });
    }
  }, [deleteRequest]);

  useEffect(() => {
    if (createRequest.isError) {
      addAlert(createRequest.error);
    }
    if (createRequest.isSuccess) {
      notesRequest.reset();

      addAlert({
        data: {
          title: 'Success',
          status: 200,
          detail: 'Success',
          messages: [{ description: t('ChangesSaved'), type: 'information' }],
        },
      });
    }
  }, [createRequest]);

  const handleSubmit = (values: any) => {
    if (isEditMode && note?.key) {
      if (values.message?.length > 0) editNote({ key: note.key, message: values.message });
      else {
        deleteNote(note.key);
      }
    } else {
      if (values.message?.length > 0) createNote({ mmsi: mmsi, message: values.message });
    }
    setShowModal(false);
  };
  const initialFormValules = useMemo(() => {
    if (note) {
      return {
        message: note.message,
      };
    } else {
      return {
        message: '',
      };
    }
  }, [note, mmsi]);

  const validationSchema = Yup.object().shape({
    message: Yup.string().max(256, t('IncorrectShipNoteLenghtError')),
  });

  return (
    <Modal
      component={
        <>
          <div>
            <Formik
              initialValues={initialFormValules}
              validationSchema={validationSchema}
              validateOnChange={true}
              onSubmit={handleSubmit}
              enableReinitialize={true}
            >
              {({ isValid, dirty, touched, errors, resetForm }) => {
                const isSubmitButtonDisabled = !isValid || !dirty;

                return (
                  <div className={styles.form_container}>
                    <Form>
                      <div className={styles.container}>
                        <div className={clsx(styles.messageField, { [styles.errorField]: !isValid })}>
                          <Field id="message" name="message" as="textarea" placeholder="" />
                        </div>

                        <div className={styles.error}>{(touched.message && errors.message) || ' '}</div>
                        <div className={styles.actions}>
                          <Button
                            disabled={isSubmitButtonDisabled}
                            className={styles.action_button}
                            label={t('ButtonLabelSave')}
                            variant="general"
                            type="submit"
                          />

                          <Button
                            className={styles.action_button}
                            type="button"
                            label={t('ButtonLabelDiscard')}
                            variant="general"
                            onClick={() => {
                              setShowModal(false);
                              resetForm();
                            }}
                          />
                        </div>
                      </div>
                    </Form>
                  </div>
                );
              }}
            </Formik>
          </div>
        </>
      }
      onClose={() => {
        setShowModal(false);
      }}
      headerTitle={t('Notes')}
      isShown={showModal}
    />
  );
};
